import { GetServerSidePropsContext } from 'next'

import { createClient } from 'shared/services/apollo/server'
import logger from 'shared/services/logger'
import { getCommonServerSideProps } from 'shared/utils/auth/server'
import { isString } from 'shared/utils/type-guards'

import { projectOverviewLabels } from '~/common/labels'
import {
  GetProjectOverview,
  GetProjectOverviewService,
} from '~/use-cases/project/overview'
import PublicProjectOverview from '~/views/components/PublicProjectOverview'
import GlobalLayout from '~/views/layouts/GlobalLayout'

interface Props {
  project: NonNullable<Required<GetProjectOverview.PublicResults['project']>>
  stats: NonNullable<Required<GetProjectOverview.PublicResults['stats']>>
}

const SharedProjectOverview = ({ project, stats }: Props) => {
  return (
    <GlobalLayout noPadding>
      <PublicProjectOverview project={project} stats={stats} />
    </GlobalLayout>
  )
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    const commonProps = await getCommonServerSideProps(context)
    const workspaceId = commonProps.userOrgData?.organization?.id
    const { projectId } = context.params || {}

    if (!isString(projectId)) throw new Error('invalid project id')

    const client = await createClient(context)
    const service = new GetProjectOverviewService(client)
    const res = await service.public({ id: projectId })

    if (!res.project) throw new Error('missing project on response')
    if (!res.stats) throw new Error('missing project stats on response')

    if (workspaceId && workspaceId === res?.project?.organization?.id) {
      return {
        redirect: {
          destination: `/app/projects/${projectId}/overview`,
          permanent: false,
        },
      }
    }

    return {
      props: {
        ...commonProps,
        project: res.project,
        stats: res.stats,
        labels: {
          projectOverview: projectOverviewLabels,
        },
      },
    }
  } catch (error) {
    logger.error(error, {
      feature: logger.Feature.SharedProjectOverview,
    })

    return {
      notFound: true,
    }
  }
}

export default SharedProjectOverview
